import React, { useState, useEffect } from 'react';
import { TextField, Popper, Paper, List, ListItem, ListItemText, ClickAwayListener } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useRegionCodes } from '../../../../common/auto-location-field/use-region-codes/use-region-codes';

interface AddressSearchFieldProps {
  label?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onLocationSelect: (location: { lat: number; long: number; address: string }) => void;
  InputProps?: any;
}

interface Suggestion {
  description: string;
  placeId: string;
}

const useStyles = makeStyles({
  suggestionsPaper: {
    maxHeight: 200,
    overflow: 'auto',
    zIndex: 1300,
  },
  suggestionItem: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
});

export const AddressSearchField = ({
  label = 'Search Address',
  placeholder = 'Enter an address...',
  value,
  onChange,
  onLocationSelect,
  InputProps: inputProps,
}: AddressSearchFieldProps) => {
  const classes = useStyles();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [autoCompleteService, setAutoCompleteService] = useState<any>(null);
  const [placesService, setPlacesService] = useState<any>(null);
  const [autoCompleteSessionToken, setAutoCompleteSessionToken] = useState<any>(null);
  const { allRegions } = useRegionCodes();

  // Initialize Google Maps services
  useEffect(() => {
    const checkGoogleMaps = () => {
      if ((window as any).google?.maps?.places) {
        setAutoCompleteService(new (window as any).google.maps.places.AutocompleteService());
        setAutoCompleteSessionToken(new (window as any).google.maps.places.AutocompleteSessionToken());

        // Create a map for PlacesService
        const map = new (window as any).google.maps.Map(document.createElement('div'));
        setPlacesService(new (window as any).google.maps.places.PlacesService(map));
      } else {
        // Try again after a short delay
        setTimeout(checkGoogleMaps, 100);
      }
    };

    checkGoogleMaps();
  }, []);

  const getPredictions = (query: string) => {
    if (!autoCompleteService || !query.trim()) {
      setSuggestions([]);
      return;
    }

    autoCompleteService.getPlacePredictions(
      {
        input: query,
        componentRestrictions: { country: allRegions },
        sessionToken: autoCompleteSessionToken,
      },
      (predictions: any) => {
        if (predictions?.length) {
          setSuggestions(
            predictions.map((pred: any) => ({
              description: pred.description,
              placeId: pred.place_id,
            }))
          );
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      }
    );
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    onChange(newValue);

    if (newValue) {
      getPredictions(newValue);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionSelect = (suggestion: Suggestion) => {
    if (!placesService) return;

    placesService.getDetails(
      {
        placeId: suggestion.placeId,
        fields: ['formatted_address', 'geometry'],
        sessionToken: autoCompleteSessionToken,
      },
      (placeResult: any) => {
        if (placeResult) {
          const lat = placeResult.geometry.location.lat();
          const lng = placeResult.geometry.location.lng();

          onChange(placeResult.formatted_address);
          onLocationSelect({
            lat,
            long: lng,
            address: placeResult.formatted_address,
          });

          // Create new session token
          if ((window as any).google?.maps?.places) {
            setAutoCompleteSessionToken(new (window as any).google.maps.places.AutocompleteSessionToken());
          }
        }
        setShowSuggestions(false);
      }
    );
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setAnchorEl(event.currentTarget);
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleClickAway = () => {
    setShowSuggestions(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div>
        <TextField
          fullWidth
          variant='outlined'
          label={label}
          placeholder={placeholder}
          value={value}
          onChange={handleInputChange}
          onFocus={handleFocus}
          InputProps={inputProps}
        />

        <Popper
          open={showSuggestions && suggestions.length > 0}
          anchorEl={anchorEl}
          placement='bottom-start'
          style={{ width: anchorEl?.offsetWidth, zIndex: 1300 }}
        >
          <Paper className={classes.suggestionsPaper}>
            <List dense>
              {suggestions.map((suggestion, _unusedIndex) => (
                <ListItem
                  key={suggestion.placeId}
                  className={classes.suggestionItem}
                  onClick={() => handleSuggestionSelect(suggestion)}
                >
                  <ListItemText primary={suggestion.description} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  );
};
